import { KeyboardEvent, useEffect, useState } from 'react';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  CheckRounded as CheckIcon,
  CloseRounded as CloseIcon,
  DeleteOutlineRounded as DeleteIcon,
  EditRounded as EditIcon,
} from '@material-ui/icons';

import { Tag } from '../../types/tag.type';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';
import { ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';

type TagsTableHeader = {
  key: string;
  label: string;
  ariaLabel?: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
};

type DialogState = { kind: 'none' } | { kind: 'delete'; tag: Tag };

const TABLE_HEADERS: TagsTableHeader[] = [
  { key: 'name', label: 'Tag', minWidth: 200, align: 'left' },
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 90, align: 'right' },
];

const COLUMN_COUNT = TABLE_HEADERS.length;

const useStyles = makeStyles((theme) => ({
  headerCell: {
    fontWeight: 'bold',
  },
  secondaryLine: {
    display: 'block',
    fontSize: 12,
    color: '#757575',
  },
  emptyCell: {
    color: '#757575',
    textAlign: 'center',
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.25rem',
  },
  nameInput: {
    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
  },
  validateButton: {
    color: theme.palette.admin.primary.main,
  },
}));

interface Props {
  tags: Tag[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  isSubmitting: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onRename: (tag: Tag, name: string) => Promise<boolean>;
  onDelete: (tag: Tag) => Promise<boolean>;
}

export default function TagsTable({
  tags,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  isSubmitting,
  onPageChange,
  onRowsPerPageChange,
  onRename,
  onDelete,
}: Props) {
  const classes = useStyles();

  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  const [isPending, setIsPending] = useState(false);

  const isBusy = isSubmitting || isPending;

  useEffect(() => {
    if (editingTagId !== null && !tags.some((tag) => tag.id === editingTagId)) {
      setEditingTagId(null);
    }
  }, [tags, editingTagId]);

  const startEditing = (tag: Tag) => {
    setDraftName(tag.name);
    setEditingTagId(tag.id);
  };

  const stopEditing = () => {
    setEditingTagId(null);
    setDraftName('');
  };

  const closeDialog = () => setDialog({ kind: 'none' });

  const submitRename = async (tag: Tag) => {
    if (isBusy) return;

    const trimmedName = draftName.trim();
    if (trimmedName === '' || trimmedName === tag.name) {
      stopEditing();
      return;
    }

    setIsPending(true);
    try {
      const isRenamed = await onRename(tag, trimmedName);
      if (isRenamed) stopEditing();
    } finally {
      setIsPending(false);
    }
  };

  const handleNameKeyDown = (event: KeyboardEvent<HTMLElement>, tag: Tag) => {
    if (event.key === 'Enter') submitRename(tag);
    if (event.key === 'Escape' && !isBusy) stopEditing();
  };

  const handleDeleteConfirm = async () => {
    if (dialog.kind !== 'delete' || isBusy) return;

    setIsPending(true);
    try {
      const isDeleted = await onDelete(dialog.tag);
      if (isDeleted) closeDialog();
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Paper>
      <TableContainer style={{ maxHeight: '65svh' }}>
        <Table size="small" stickyHeader aria-label="Liste des tags">
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <TableCell
                  key={header.key}
                  aria-label={header.ariaLabel}
                  align={header.align}
                  className={classes.headerCell}
                  style={{ minWidth: header.minWidth }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={COLUMN_COUNT} />}
          {!isLoading && (
            <TableBody>
              {tags.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className={classes.emptyCell}>
                    Aucun tag trouvé : créez-en un depuis le formulaire ci-contre.
                  </TableCell>
                </TableRow>
              )}
              {tags.map((tag) => {
                const isEditing = tag.id === editingTagId;
                const areActionsLocked = editingTagId !== null || isBusy;

                return (
                  <TableRow hover key={tag.id}>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          autoFocus
                          fullWidth
                          size="small"
                          variant="outlined"
                          className={classes.nameInput}
                          inputProps={{ 'aria-label': 'Renommer le tag' }}
                          InputProps={{ readOnly: isPending }}
                          value={draftName}
                          onChange={(event) => setDraftName(event.target.value)}
                          onKeyDown={(event) => handleNameKeyDown(event, tag)}
                        />
                      ) : (
                        <>
                          {tag.name}
                          <span className={classes.secondaryLine}>{tag.slug}</span>
                        </>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <div className={classes.rowActions}>
                        {isEditing ? (
                          <>
                            <IconButton
                              size="small"
                              aria-label="Valider"
                              className={classes.validateButton}
                              disabled={isBusy}
                              onClick={() => submitRename(tag)}
                            >
                              <CheckIcon />
                            </IconButton>
                            <IconButton size="small" aria-label="Annuler" disabled={isBusy} onClick={stopEditing}>
                              <CloseIcon />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              aria-label="Modifier"
                              disabled={areActionsLocked}
                              onClick={() => startEditing(tag)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Supprimer"
                              disabled={areActionsLocked}
                              onClick={() => setDialog({ kind: 'delete', tag })}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
      />
      {dialog.kind === 'delete' && (
        <ConfirmActionDialog
          open
          title="Supprimer le tag"
          description={`Le tag « ${dialog.tag.name} » sera retiré des produits, paniers et producteurs.`}
          confirmLabel="Supprimer"
          isSubmitting={isBusy}
          onConfirm={handleDeleteConfirm}
          onClose={closeDialog}
        />
      )}
    </Paper>
  );
}
